import React, { useContext, useLayoutEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { List } from "react-virtualized";

import { TFlatVirtualizedSportItem } from "@data-types/offer/live/TFlatVirtualizedSportItem";
import { VirtualizedListItemType } from "@data-types/offer/live/VirtualizedListItemTypeEnum";
import { Event, EventOffer } from "@features/offer/desktop/event-row";
import EventRenderErrorBoundary from "@features/offer/desktop/view/EventRenderErrorBoundary";
import LiveTournamentHeader from "@features/offer/desktop/view/components/LiveTournamentHeader";
import { EventContext, OfferOptionsContext } from "@gp/components";
import {
	useMultiListScrollOffset,
	useWsProps,
} from "@lib/virtualization-components";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import LiveViewStore from "@state/stores/offer/live/LiveViewStore";
import {
	useGetAdditionalOfferEvent,
	useIsAdditionalOfferVisible,
} from "@utils/offer";
import classNames from "classnames";
import { observer } from "mobx-react";

const UNIT = 4;
const OFFER_HEADER = UNIT * 12;
const OFFER_ROW = UNIT * 14;
const OFFER_HT = UNIT * 11;

export default observer(function VirtualizedSportOffer(props: {
	isScrollRestoreWorking: boolean;
	flatSport: TFlatVirtualizedSportItem[];
	isSpCollapsed: boolean;
	store: LiveViewStore;
	listHeight: number;
	parentWrapperDiv: HTMLDivElement | null;
}) {
	const t = useTranslation().t;
	const sport = props.flatSport[0].sport;

	const isAdditionalOfferVisible = useIsAdditionalOfferVisible();
	const additionalOfferEventId = useGetAdditionalOfferEvent()?.eventId;
	const favoriteStore = useContext(FavoritesStoreContext);
	const options = useContext(OfferOptionsContext);

	const wsProps = useWsProps();
	const listRef = useRef<List>(null);
	const [listWrapperDivRef, setListWrapperDivRef] =
		React.useState<HTMLDivElement | null>(null);

	const rowCount = props.flatSport.length - 1;

	useLayoutEffect(() => {
		listRef.current?.recomputeRowHeights();
		listRef.current?.measureAllRows();
	}, [favoriteStore.userFavoriteEventsSet, props.flatSport]);

	const listScrollTop = useMultiListScrollOffset(
		props.parentWrapperDiv || listWrapperDivRef,
		props.store.eventsInSports.sports[0] === sport,
		window,
		props.listHeight
	);

	if (sport == null) {
		console.error(
			"Expected sport to be first in flat sport list, got nill"
		);
		return null;
	}

	const _getRowHeight = (getRowHeightProps: { index: number }): number => {
		const item = props.flatSport[getRowHeightProps.index + 1];

		if (item == null) {
			console.error(
				`List item missing at index ${getRowHeightProps.index}.`
			);
			return 0;
		}

		// Half time offer
		if (
			item.type == VirtualizedListItemType.secondaryEvent &&
			item.isSecondary
		) {
			return OFFER_HT;
		}

		// Tournament headers on grouped offer
		if (item.type == VirtualizedListItemType.groupByTournament) {
			return OFFER_HEADER;
		}

		// event main and secondary offer have the same height
		return OFFER_ROW;
	};

	const _renderRow = (renderRowProps: {
		index: number;
		style: React.CSSProperties;
		key: React.Key;
	}) => {
		const item = props.flatSport[renderRowProps.index + 1];

		if (item == null) {
			console.error(
				`List item missing at index ${renderRowProps.index}.`
			);
			return null;
		}

		if (props.isScrollRestoreWorking) {
			return (
				<div style={renderRowProps.style} key={renderRowProps.key} />
			);
		}

		if (item.type == VirtualizedListItemType.groupByTournament) {
			return (
				<div
					key={item.tournament}
					className="offer--virtual__head offer--virtual__head--tournament u-mar--top--reset"
					style={renderRowProps.style}
				>
					<LiveTournamentHeader
						key={item.tournament}
						sport={sport}
						categoryId={item.categoryId!}
						tournamentId={item.tournament}
						setHidden={() =>
							props.store.toggleTournamentCollapse(
								item.tournament
							)
						}
						hidden={props.store.collapsedTournaments.includes(
							item.tournament
						)}
						isAdditionalOfferVisible={isAdditionalOfferVisible}
					/>
				</div>
			);
		}

		if (item.event == null) {
			console.error(
				`Event missing at list item at index ${renderRowProps.index}.`
			);
			return null;
		}

		// render half time offer
		if (item.isSecondary) {
			return (
				<div
					key={item.event.id + "-secondary"}
					className={classNames("offer--virtual__tbody offer__ht", {
						"is-top-event":
							//@ts-expect-error
							!options?.shouldHideTopTag && item.event.isTopEvent,
						"is-selected": additionalOfferEventId === item.event.id,
					})}
					style={renderRowProps.style}
				>
					<div className="offer__body offer--virtual__body">
						<EventContext.Provider value={item.event}>
							<div
								className={classNames(
									"offer__body__row offer__ht__row",
									{
										"offer__ht__row--sml":
											props.store.period == "upcoming",
										"is-collapsed":
											isAdditionalOfferVisible,
										odd: renderRowProps.index % 2 !== 0,
									}
								)}
							>
								<div className="offer__body__data offer__ht__inner">
									<span className="offer__ht__title">
										{t("LIVE.FIRST_HALF_TIME")}
									</span>
								</div>

								<EventOffer columns={item.secondaryOffer} />
							</div>
						</EventContext.Provider>
					</div>
				</div>
			);
		}

		// render event
		return (
			<div
				className={
					"offer--virtual__tbody" +
					//@ts-expect-error
					(!options?.shouldHideTopTag && item.event.isTopEvent
						? " is-top-event"
						: "")
				}
				key={item.event.id}
				style={renderRowProps.style}
			>
				<EventRenderErrorBoundary event={item.event}>
					<div
						className={classNames(
							"offer__body offer--virtual__body",
							{
								offer__main: item.secondaryCount > 0,
							}
						)}
					>
						<EventContext.Provider value={item.event}>
							<Event
								isAlternate={renderRowProps.index % 2 === 0}
								hasSecondaryOffer={item.secondaryCount > 0}
								noSecondaryOffer
								secondaryOfferCount={item.secondaryCount}
							/>
						</EventContext.Provider>
					</div>
				</EventRenderErrorBoundary>
			</div>
		);
	};

	return (
		<div ref={setListWrapperDivRef}>
			<List
				ref={listRef}
				autoHeight
				height={wsProps.height}
				width={wsProps.width}
				overscanRowCount={2}
				rowHeight={_getRowHeight}
				rowCount={rowCount}
				rowRenderer={_renderRow}
				scrollTop={listScrollTop}
				style={{ width: "100%" }}
			/>
		</div>
	);
});
